/**
 * TimeFilter - green text link dropdown for filtering list pages by time range.
 *
 * Renders inline in the page subtitle. Opens a dropdown with preset ranges
 * and a custom date range option.
 */

import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { cn } from "@/shared/lib/utils";
import { useFormatting } from "@/admin/hooks/use-formatting";

type TimeRange =
  | "this_week"
  | "this_month"
  | "last_month"
  | "all_time"
  | "custom";

interface TimeFilterProps {
  value: TimeRange;
  customRange?: { from: string; to: string };
  onChange: (range: TimeRange, custom?: { from: string; to: string }) => void;
  /** Restyle the trigger, e.g. as a toolbar button beside other controls. */
  triggerClassName?: string;
}

export type { TimeRange };

const PRESET_OPTIONS: Array<{ value: TimeRange; label: string }> = [
  { value: "this_week", label: "This week" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "all_time", label: "All time" },
];

function getDisplayLabel(
  value: TimeRange,
  locale: string,
  customRange?: { from: string; to: string },
): string {
  if (value === "custom" && customRange) {
    const fmt = (d: string) => {
      const date = new Date(d);
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(date);
    };
    return `${fmt(customRange.from)} \u2192 ${fmt(customRange.to)}`;
  }
  return PRESET_OPTIONS.find((o) => o.value === value)?.label ?? "All time";
}

export function TimeFilter({
  value,
  customRange,
  onChange,
  triggerClassName,
}: TimeFilterProps) {
  const { t } = useTranslation();
  const { locale } = useFormatting();
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [fromDate, setFromDate] = useState(customRange?.from ?? "");
  const [toDate, setToDate] = useState(customRange?.to ?? "");
  // Apply stays enabled; a click with a date missing points at it instead.
  const [attempted, setAttempted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    // While the custom-range panel is open, the DatePickers render their
    // calendar popovers through portals on document.body - clicking a day
    // there must not close the TimeFilter. Skip the outside-click handler
    // entirely in custom mode; user closes it via Apply / Clear / trigger.
    if (showCustom) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, showCustom]);

  function selectPreset(range: TimeRange) {
    onChange(range);
    setOpen(false);
    setShowCustom(false);
    setAttempted(false);
  }

  function applyCustom() {
    if (!fromDate || !toDate) {
      setAttempted(true);
      return;
    }
    // A reversed range means the same window; order it rather than refuse it.
    const [from, to] = fromDate <= toDate ? [fromDate, toDate] : [toDate, fromDate];
    onChange("custom", { from, to });
    setOpen(false);
    setShowCustom(false);
    setAttempted(false);
  }

  return (
    <div className="relative inline-flex" ref={ref}>
      <Button
        variant="link"
        onClick={() => setOpen(!open)}
        className={cn(
          "inline-flex cursor-pointer items-center gap-1 border-none bg-transparent pb-px text-base font-normal text-[var(--green)] transition-colors",
          triggerClassName,
        )}
      >
        <span>{getDisplayLabel(value, locale, customRange)}</span>
        <ChevronDown
          size={9}
          className={cn("transition-transform duration-150", open && "rotate-180")}
        />
      </Button>
      {open && (
        <div
          className={cn(
            "absolute left-0 top-full z-50 mt-1 overflow-hidden rounded-[8px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-[var(--bg)] shadow-[var(--shadow-float)] animate-[cvPanelEnter_0.18s_cubic-bezier(0.16,1,0.3,1)]",
            showCustom ? "w-[280px]" : "w-[200px]",
          )}
        >
          {!showCustom ? (
            <>
              {PRESET_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  variant="ghost"
                  onClick={() => selectPreset(opt.value)}
                  className={`flex w-full cursor-pointer items-center justify-between border-b-[0.5px] border-b-[var(--rule-light)] px-3.5 py-2.5 text-left text-base transition-colors duration-100 ${
                    value === opt.value
                      ? "text-[var(--green)] bg-[var(--green-bg)]"
                      : "text-[var(--ink)] hover:bg-[var(--bg-subtle)]"
                  }`}
                >
                  {opt.label}
                  {value === opt.value && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    >
                      <polyline points="1,6 4,10 11,2" />
                    </svg>
                  )}
                </Button>
              ))}
              <div className="h-[0.5px] bg-[var(--rule)]" />
              <Button
                variant="ghost"
                onClick={() => setShowCustom(true)}
                className="flex w-full cursor-pointer items-center gap-2 px-3.5 py-2.5 text-left text-base text-[var(--green)] transition-colors duration-100 hover:bg-[var(--bg-subtle)]"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                >
                  <rect x="2" y="3" width="12" height="11" rx="2" />
                  <line x1="2" y1="7" x2="14" y2="7" />
                  <line x1="5" y1="1" x2="5" y2="5" />
                  <line x1="11" y1="1" x2="11" y2="5" />
                </svg>
                Custom range...
              </Button>
            </>
          ) : (
            <div className="p-3.5">
              <p
                className="mb-3 text-xs uppercase tracking-[0.12em] text-[var(--ink-3)]"
              >
                {t("timeFilter.customRange", { defaultValue: "Custom range" })}
              </p>
              <div className="flex flex-col gap-2 mb-3">
                <DatePicker
                  value={fromDate}
                  onChange={setFromDate}
                  placeholder={t("timeFilter.fromDate", { defaultValue: "From date" })}
                  hasError={attempted && !fromDate}
                />
                <DatePicker
                  value={toDate}
                  onChange={setToDate}
                  placeholder={t("timeFilter.toDate", { defaultValue: "To date" })}
                  hasError={attempted && !toDate}
                />
                {attempted && (!fromDate || !toDate) && (
                  <p role="alert" className="text-xs text-[var(--status-error)]">
                    {t("timeFilter.bothDatesRequired", {
                      defaultValue: "Choose both dates.",
                    })}
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between">
                <Button
                  variant="link"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                    selectPreset("all_time");
                  }}
                  className="cursor-pointer border-none bg-transparent text-sm uppercase tracking-[0.07em] text-[var(--ink-3)]"
                >
                  {t("common.clear", { defaultValue: "Clear" })}
                </Button>
                <Button
                  onClick={applyCustom}
                  className="cursor-pointer rounded-[4px] border-none bg-[var(--green)] px-3.5 py-1.5 text-sm uppercase tracking-[0.07em] text-white hover:bg-[var(--green-hover)]"
                >
                  {t("common.apply", { defaultValue: "Apply" })}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
